// App.jsx — top-level router.
//
// Route structure:
//   /login                       (public)
//   /forgot-password             (public)
//   /reset-password/:token       (public, deep-link from email)
//   /accept-invitation/:token    (public, deep-link from email)
//   /abhisuperadmin/*            (gated: super_admin only)
//   /abhiadmin/*                 (gated: admin only)
//   /employee/*                  (gated: employee only)
//   /                            (smart redirect based on auth state)
//   *                            (404 → redirects to role home or /login)
//
// The role-gated routes mount AdminPanel inside a NavProvider so that
// nested URL segments (e.g. /abhisuperadmin/leads/all) map to AdminPanel's
// internal `active` string ("leads.all").

import { Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import AcceptInvitation from './pages/auth/AcceptInvitation';
import AdminPanelRouted from './components/layout/AdminPanelRouted';
import { useAuth, rolePrefix } from './lib/AuthContext';
import { Loader2 } from 'lucide-react';

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#faf9f5]">
        <Loader2 className="w-5 h-5 animate-spin text-[#162a52]" />
      </div>
    );
  }
  return <Navigate to={user ? `${rolePrefix(user.role)}/dashboard` : '/portal-x7k3'} replace />;
}

export default function App() {
  return (
    <Routes>
      {/* Public auth pages */}
      <Route path="/portal-x7k3" element={<Login />} />
      <Route path="/portal-x7k3/recover" element={<ForgotPassword />} />
      <Route path="/portal-x7k3/reset/:token" element={<ResetPassword />} />
      <Route path="/portal-x7k3/invite/:token" element={<AcceptInvitation />} />

      {/* Role-gated admin shells.
          The trailing /* lets AdminPanel handle nested sub-paths via NavProvider. */}
      <Route
        path="/abhisuperadmin/*"
        element={<AdminPanelRouted role="super_admin" rolePrefix="/abhisuperadmin" />}
      />
      <Route
        path="/abhiadmin/*"
        element={<AdminPanelRouted role="admin" rolePrefix="/abhiadmin" />}
      />
      <Route
        path="/employee/*"
        element={<AdminPanelRouted role="employee" rolePrefix="/employee" />}
      />

      {/* Root and 404 → smart redirect */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}
